import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/home/buttons';
import { Input } from '../../components/home/input';
import { requestYoutubePreview } from '../../service/api.service';

function isValidYoutubeUrl(value: string) {
    try {
        const url = new URL(value);
        const host = url.hostname.replace(/^www\./, '');
        return host === 'youtube.com' || host === 'youtu.be' || host.endsWith('.youtube.com');
    } catch (error) {
        return false;
    }
}

export function YoutubeTab() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [url, setUrl] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    function validateUrl(): string | null {
        const trimmed = url.trim();
        if (!trimmed) return t('route.home.txtYoutubeMissing');
        if (!isValidYoutubeUrl(trimmed)) return t('route.home.txtYoutubeInvalid');
        return null;
    }

    function onInputChange(event: ChangeEvent<HTMLInputElement>) {
        setUrl(event.target.value);
        setError(null);
    }

    async function onBtnSubmit() {
        const validationError = validateUrl();
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsPreviewing(true);
        setError(null);

        const trimmed = url.trim();
        const result = await requestYoutubePreview(trimmed);
        if (!isMountedRef.current) return;

        if (result.error) {
            setIsPreviewing(false);
            if (result.error.code === 'youtube_too_long') {
                setError(t('route.home.txtYoutubeTooLong'));
                return;
            }
            if (result.error.code === 'invalid_youtube_url' || result.error.code === 'missing_youtube_url') {
                setError(t('route.home.txtYoutubeInvalid'));
                return;
            }
            setError(t('route.home.txtPreviewFailed'));
            return;
        }

        if (!result.data) {
            setIsPreviewing(false);
            setError(t('route.home.txtPreviewFailed'));
            return;
        }

        navigate(`/preview/${encodeURIComponent(result.data.jobId)}`);
    }

    return (
        <div id="source-panel-youtube" role="tabpanel" aria-label={t('route.home.txtInputTypeYoutube')} className="space-y-2">
            <label className="text-xs uppercase tracking-[0.25em] text-secondary">{t('route.home.txtYoutubeLabel')}</label>

            <Input type="url" placeholder={t('route.home.txtYoutubePlaceholder')} value={url} onChange={onInputChange} disabled={isPreviewing} />
            <p className="text-xs text-secondary">{t('route.home.txtYoutubeHelper')}</p>

            {!!error && <p className="text-sm text-danger">{error}</p>}

            <Button onClick={onBtnSubmit} disabled={isPreviewing} isLoading={isPreviewing} className="w-full">
                {t('route.home.txtPreviewAction')}
            </Button>
        </div>
    );
}
